import React from 'react';
import { motion } from 'framer-motion';
import { Drawer } from 'vaul';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import Scene from './Scene';

const MobileHero = () => {
    return (
        <section className="relative min-h-[90dvh] flex flex-col justify-center pb-20 overflow-hidden bg-slate-50">
            {/* --- Background Finish: Radial Gradient + soft noise --- */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_var(--tw-gradient-stops))] from-indigo-50/50 via-slate-50 to-slate-50 z-0" />

            {/* --- 3D Background (Globe) --- */}
            <div className="absolute inset-0 z-0 mix-blend-multiply translate-y-12">
                <Scene />
                {/* Lighter Overlay Gradient: Reduced fog/blur for clearer background */}
                <div className="absolute inset-0 bg-gradient-to-b from-slate-50/20 via-slate-50/60 to-transparent" />
            </div>

            {/* --- Mobile Logo (Top Left - Aligned Grid) --- */}
            <div className="absolute top-8 left-6 z-20">
                <div className="relative h-24 w-72">
                    <AppImage
                        src="/Logo_Home_Herosection.png"
                        alt="Site Map Genius"
                        fill
                        className="object-contain object-left !bg-transparent !animate-none"
                        style={{ objectFit: 'contain' }}
                        priority
                    />
                </div>
            </div>

            {/* --- Main Content Area --- */}
            {/* Using Consistent Padding: px-6 (24px) */}
            <div className="relative z-10 px-6 w-full mt-24">
                <motion.div
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    className="flex flex-col items-start text-left"
                >

                    {/* Headline */}
                    <h1 className="text-4xl xs:text-5xl font-black text-slate-900 leading-[1.1] tracking-tight mb-5 w-full">
                        We Don't Just Build;
                        <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-600 drop-shadow-sm">
                            We Elevate.
                        </span>
                    </h1>

                    {/* Description - Increased contrast & line-height */}
                    <p className="text-base text-slate-700 font-medium leading-[1.75] w-full mb-10 max-w-[90%]">
                        Complete digital ecosystems combining full-stack development mastery with insider SEO strategies. Transform your brand visibility with measurable results.
                    </p>

                    {/* Actions - CTA */}
                    <div className="w-full mb-10">
                        <Drawer.Root>
                            <Drawer.Trigger asChild>
                                <button className="w-full py-4 bg-slate-900 text-white rounded-2xl font-bold text-lg shadow-xl shadow-slate-900/10 active:scale-[0.98] transition-all flex items-center justify-center space-x-3 group relative overflow-hidden">
                                    <span className="relative z-10">Start Your Project</span>
                                    <Icon name="ArrowRightIcon" className="relative z-10 w-5 h-5 text-indigo-400 group-hover:translate-x-1 transition-transform" />
                                    {/* Button Shine Effect */}
                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full animate-shine z-0 pointer-events-none" />
                                </button>
                            </Drawer.Trigger>
                            <Drawer.Portal>
                                <Drawer.Overlay className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50" />
                                <Drawer.Content className="bg-white flex flex-col rounded-t-[32px] h-[85vh] mt-24 fixed bottom-0 left-0 right-0 z-50 outline-none">
                                    {/* Handle */}
                                    <div className="p-4 bg-white rounded-t-[32px] flex-shrink-0">
                                        <div className="mx-auto w-12 h-1.5 flex-shrink-0 rounded-full bg-slate-200 mb-2" />
                                    </div>

                                    {/* Drawer Body - Scrollable */}
                                    <div className="p-6 bg-white flex-1 overflow-y-auto">
                                        <div className="max-w-md mx-auto">
                                            <Drawer.Title className="font-bold text-2xl mb-2 text-slate-900">
                                                Let's Build Something Great.
                                            </Drawer.Title>
                                            <Drawer.Description className="text-slate-500 mb-8">
                                                Choose how you want to get started.
                                            </Drawer.Description>

                                            <div className="space-y-3">
                                                <Link
                                                    href="/contact?type=project"
                                                    className="block p-4 rounded-xl border border-slate-200 bg-slate-50 hover:bg-indigo-50 hover:border-indigo-200 transition-colors flex items-center space-x-4"
                                                >
                                                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm text-indigo-600">
                                                        <Icon name="RocketLaunchIcon" size={20} />
                                                    </div>
                                                    <div>
                                                        <div className="font-bold text-slate-900">New Project Inquiry</div>
                                                        <div className="text-xs text-slate-500">I have a project in mind</div>
                                                    </div>
                                                </Link>

                                                <Link
                                                    href="/contact?type=audit"
                                                    className="block p-4 rounded-xl border border-slate-200 bg-slate-50 hover:bg-blue-50 hover:border-blue-200 transition-colors flex items-center space-x-4"
                                                >
                                                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm text-blue-600">
                                                        <Icon name="MagnifyingGlassIcon" size={20} />
                                                    </div>
                                                    <div>
                                                        <div className="font-bold text-slate-900">Free SEO Audit</div>
                                                        <div className="text-xs text-slate-500">Check my site's health</div>
                                                    </div>
                                                </Link>

                                                <Link
                                                    href="tel:+918686284396"
                                                    className="block p-4 rounded-xl border border-slate-200 bg-slate-50 hover:bg-green-50 hover:border-green-200 transition-colors flex items-center space-x-4"
                                                >
                                                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm text-green-600">
                                                        <Icon name="PhoneIcon" size={20} />
                                                    </div>
                                                    <div>
                                                        <div className="font-bold text-slate-900">Call Us Directly</div>
                                                        <div className="text-xs text-slate-500">+91 8686284396</div>
                                                    </div>
                                                </Link>
                                            </div>

                                            <div className="mt-8 pt-8 border-t border-slate-100 text-center">
                                                <Link href="/contact" className="text-indigo-600 font-semibold text-sm">
                                                    View all contact options &rarr;
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                </Drawer.Content>
                            </Drawer.Portal>
                        </Drawer.Root>
                    </div>

                    {/* Social Proof - "Modern Glass Pill" */}
                    <div className="flex items-center justify-start w-full">
                        <div className="inline-flex items-center p-1.5 px-4 bg-white/60 backdrop-blur-md border border-slate-200/50 rounded-full shadow-sm">
                            {/* Rating */}
                            <div className="flex items-center space-x-1.5 mr-4">
                                <div className="flex space-x-0.5">
                                    {[1, 2, 3, 4, 5].map(i => (
                                        <Icon key={i} name="StarIcon" className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
                                    ))}
                                </div>
                                <span className="font-bold text-slate-900 text-sm">4.9</span>
                            </div>

                            {/* Divider */}
                            <div className="w-px h-4 bg-slate-300 mr-4" />

                            {/* Trusted */}
                            <div className="flex items-center space-x-2">
                                <span className="text-sm text-slate-600 font-medium">
                                    Trusted by <span className="font-bold text-slate-900">25+ Businesses</span>
                                </span>
                            </div>
                        </div>
                    </div>

                </motion.div>
            </div>

            {/* --- Infinite Service Ticker --- */}
            <div className="absolute bottom-8 left-0 right-0 z-20 overflow-hidden py-2 border-t border-slate-200/50 bg-white/30 backdrop-blur-sm flex">
                {/* Strip 1 */}
                <div className="flex whitespace-nowrap animate-marquee min-w-full flex-shrink-0 items-center">
                    {[1, 2, 3, 4].map((i) => (
                        <span key={i} className="mx-4 text-[10px] sm:text-xs font-bold text-slate-600 tracking-[0.2em] uppercase">
                            STRATEGY • DESIGN • DEVELOPMENT • SEO • BRANDING • GROWTH •
                        </span>
                    ))}
                </div>
                {/* Strip 2 (Duplicate for Seamless Loop) */}
                <div className="flex whitespace-nowrap animate-marquee min-w-full flex-shrink-0 items-center">
                    {[1, 2, 3, 4].map((i) => (
                        <span key={`dup-${i}`} className="mx-4 text-[10px] sm:text-xs font-bold text-slate-600 tracking-[0.2em] uppercase">
                            STRATEGY • DESIGN • DEVELOPMENT • SEO • BRANDING • GROWTH •
                        </span>
                    ))}
                </div>
            </div>
        </section >
    );
};

export default MobileHero;
